<script setup lang="ts">
  import SelectOption from '@/interfaces/SelectOption';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  interface Props {
    modelValue: any;
    type?: 'string' | 'number' | 'boolean';
    options: SelectOption[];
    defaultValue?: any;
    disabled?: boolean;
    isValid?: boolean;
    serverValue?: any; // 서버 설정 값
  }

  const props = withDefaults(defineProps<Props>(), {
    type: 'string',
    disabled: false,
  });

  const emits = defineEmits(['update:modelValue']);

  const selectedValue = computed({
    get() {
      switch (props.type) {
        case 'number':
          const numberValue = props.modelValue ?? props.defaultValue;
          return numberValue != null ? String(numberValue) : '';
        case 'boolean':
          const booleanValue = props.modelValue ?? props.defaultValue;
          if (booleanValue === true) return 'true';
          if (booleanValue === false) return 'false';
          return '';
        default:
          return props.modelValue ?? props.defaultValue ?? '';
      }
    },
    set(value) {
      switch (props.type) {
        case 'number':
          emits('update:modelValue', value === '' ? '' : Number(value));
          break;
        case 'boolean':
          if (value === 'true') emits('update:modelValue', true);
          else if (value === 'false') emits('update:modelValue', false);
          else emits('update:modelValue', '');
          break;
        default:
          emits('update:modelValue', value);
          break;
      }

      //emits('update:modelValue', value);
    },
  });

  const displayOptions = computed(() => {
    if (props.serverValue !== undefined) {
      const serverOptionName = props.options.find((option) => option.value === props.serverValue)?.text;

      const optionName = serverOptionName
        ? `(${t('select.default_server')}) - ${serverOptionName}`
        : `(${t('select.default_server')})`;

      return [{ text: optionName, value: '' }, ...props.options.filter((option) => !option.hidden)];
    }
    return props.options;
  });
</script>

<template>
  <div class="base-select" :class="{ 'no-valid': !isValid }">
    <select v-model="selectedValue" :disabled="disabled">
      <option v-for="(option, index) in displayOptions" :key="index" :value="option.value">
        {{ option.text }}
      </option>
    </select>
  </div>
</template>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .base-select {
    select {
      width: 100%;
      height: 4.4776vw;
      padding: 0.7463vw;
      border: 1px solid #dededf;
      border-radius: 4px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10.004' height='5.675' viewBox='0 0 10.004 5.675'%3E%3Cpath id='caret-down' d='M11.968,192h8.658a.672.672,0,0,1,.474,1.147l-4.327,4.331a.674.674,0,0,1-.952,0l-4.327-4.331A.672.672,0,0,1,11.968,192Z' transform='translate(-11.295 -192)' /%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0.8955vw center;
      font-size: 2.0896vw;
      @media (max-width: vars.$md) {
        height: 30px;
        font-size: 14px;
      }
    }
  }
</style>
